import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { GameState, Resource, DroneState, DronesState } from '../types';
import { UPGRADES } from '../data/upgrades';
import { MINING_DATA } from '../data/mining';
import { FARMING_DATA, PLOT_UNLOCK_LEVELS } from '../data/farming';
import { CRAFTING_RECIPES } from '../data/crafting';

interface StoreState extends GameState {
    addResource: (res: Resource, amount: number) => void;
    removeResource: (res: Resource, amount: number) => boolean;
    gainXp: (skillId: string, amount: number) => void;
    setMarketPrice: (res: Resource, price: number) => void;
    tick: (dt: number) => void;

    // Debug / Cheat actions
    setResource: (res: Resource, amount: number) => void;
    setSkillLevel: (skillId: string, level: number) => void;
    unlockAllUpgrades: () => void;


    // Market Actions
    createListing: (slotId: number, itemId: Resource, type: import('../types').OrderType, amount: number, price: number) => boolean;
    cancelListing: (slotId: number) => boolean;
    claimListing: (slotId: number) => boolean;
    unlockNextSlot: () => boolean;
    processMarketSim: () => void; // Called by game loop

    mineAction: (res: Resource) => boolean;
    plantAction: (plotId: number, crop: import('../types').CropType) => boolean;
    harvestAction: (plotId: number) => boolean;
    assignGnome: (plotId: number) => void;
    removeGnome: (plotId: number) => void;
    castRainTotem: () => void;
    mutateCrop: (plotId: number) => void;

    // Crafting
    startCraft: (slotId: number, recipeId: string) => boolean;
    claimCraft: (slotId: number) => boolean;
    startAutoCraft: (slotId: number, recipeId: string) => void;
    stopAutoCraft: (slotId: number) => void;

    // Upgrades
    purchaseUpgrade: (upgradeId: string) => boolean;
    setLastListingItem: (item: Resource) => void;
    setLastListingType: (type: import('../types').OrderType) => void;

    // Dynamite
    throwDynamite: (score: number, targetRes: Resource) => Partial<Record<Resource, number>> | null;

    // Drones
    setDroneTarget: (droneId: string, ore: Resource | null) => void;
    tickDrones: (dt: number) => void;

    // Cooldown support
    miningReadyAt: number;
}

// Helper to init resources
const RESOURCE_KEYS: Resource[] = [
    'gold', 'wood',
    ...MINING_DATA.map(m => m.id),
    ...FARMING_DATA.map(f => f.id),
    ...CRAFTING_RECIPES.flatMap(r => Object.keys(r.output) as Resource[])
];

const INITIAL_RESOURCES: Record<Resource, number> = RESOURCE_KEYS.reduce((acc, key) => {
    acc[key] = 0;
    return acc;
}, {} as Record<Resource, number>);

// Helper to init market
// farming/wood base prices
const BASE_MARKET_DEFS: Partial<Record<Resource, number>> = {
    wood: 10,
    gold: 1, // currency
};

// Add farming items from data
FARMING_DATA.forEach(crop => {
    BASE_MARKET_DEFS[crop.id] = Math.max(1, Math.floor(crop.cost * 1.5 + (crop.duration / 1000)));
});

const INITIAL_MARKET: any = {};
// Add manual items
Object.entries(BASE_MARKET_DEFS).forEach(([key, price]) => {
    INITIAL_MARKET[key] = { id: key, name: key.charAt(0).toUpperCase() + key.slice(1), basePrice: price, currentPrice: price, volatility: 0.1, history: [price] };
});
// Add mining items
MINING_DATA.forEach(min => {
    // Price roughly 2x XP or manually tuned
    const price = Math.max(1, Math.floor(min.baseXp * 0.8) + min.tier * 2);
    INITIAL_MARKET[min.id] = {
        id: min.id,
        name: min.name,
        basePrice: price,
        currentPrice: price,
        volatility: 0.1 + (min.tier * 0.05), // Higher tier = more volatile
        history: [price]
    };
});
// Add crafting items
CRAFTING_RECIPES.forEach(r => {
    Object.keys(r.output).forEach(outRes => {
        const key = outRes as Resource;
        if (INITIAL_MARKET[key]) return; // Already added
        // Base price is proportional to input cost + time
        // Just rough estimate: 2x inputs
        let cost = 0;
        Object.entries(r.input).forEach(([inRes, amount]) => {
            const inPrice = INITIAL_MARKET[inRes as Resource]?.basePrice ?? 5;
            cost += inPrice * (amount as number);
        });
        const price = Math.floor(cost * 1.5 + (r.duration / 1000));

        // Find name if simple resource, or use recipe name if 1 output
        let name = key.split('_').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');

        INITIAL_MARKET[key] = {
            id: key,
            name: name,
            basePrice: price,
            currentPrice: price,
            volatility: 0.05,
            history: [price]
        };
    });
});



export const useStore = create<StoreState>()(
    persist(
        (set, get) => ({
            resources: INITIAL_RESOURCES,
            skills: {
                mining: { id: 'mining', name: 'Mining', level: 1, xp: 0 },
                farming: { id: 'farming', name: 'Farming', level: 1, xp: 0 },
                crafting: { id: 'crafting', name: 'Crafting', level: 1, xp: 0 },
            },
            market: INITIAL_MARKET,
            marketSlots: Array.from({ length: 10 }, (_, i) => ({
                id: i,
                unlocked: i < 3 // Start with 3 slots
            })),
            tradeHistory: [],
            farming: {
                plots: Array.from({ length: 5 }, (_, i) => ({ id: i, unlocked: i === 0 })),
            },
            crafting: {
                slots: Array.from({ length: 5 }, (_, i) => ({ id: i, unlocked: i === 0 })),
            },
            drones: {
                unlockedTiers: [],
                activeDrones: [],
                maxDrones: 0,
            },
            upgrades: {}, // Start with no upgrades
            lastSaveTime: Date.now(),
            miningReadyAt: 0,

            addResource: (res, amount) =>
                set((state) => ({
                    resources: { ...state.resources, [res]: (state.resources[res] ?? 0) + amount },
                })),

            setResource: (res, amount) =>
                set((state) => ({
                    resources: { ...state.resources, [res]: amount },
                })),

            removeResource: (res, amount) => {
                const current = get().resources[res] ?? 0;
                if (current < amount) return false;
                set((state) => ({
                    resources: { ...state.resources, [res]: current - amount },
                }));
                return true;
            },

            gainXp: (skillId, amount) =>
                set((state) => {
                    const skill = state.skills[skillId];
                    if (!skill) return state;

                    const newXp = skill.xp + amount;
                    // Formula: Level = floor(sqrt(newXp / 50)) + 1
                    const newLevel = Math.floor(Math.sqrt(newXp / 50)) + 1;

                    // NEW: Check for unlocks (specifically Farming)
                    let newFarmingState = state.farming;
                    if (skillId === 'farming' && newLevel > skill.level) {
                        // Level up happened! Check plot unlocks
                        const updatedPlots = state.farming.plots.map(p => {
                            const reqLevel = PLOT_UNLOCK_LEVELS[p.id];
                            if (!p.unlocked && reqLevel !== undefined && newLevel >= reqLevel) {
                                return { ...p, unlocked: true };
                            }
                            return p;
                        });
                        newFarmingState = { ...state.farming, plots: updatedPlots };
                    }

                    return {
                        skills: {
                            ...state.skills,
                            [skillId]: { ...skill, xp: newXp, level: newLevel },
                        },
                        farming: newFarmingState
                    };
                }),

            setSkillLevel: (skillId, level) =>
                set((state) => {
                    const skill = state.skills[skillId];
                    if (!skill) return state;

                    // Inverse formula: XP = (Level - 1)^2 * 50
                    const newXp = Math.pow(level - 1, 2) * 50;

                    return {
                        skills: {
                            ...state.skills,
                            [skillId]: { ...skill, level, xp: newXp },
                        },
                    };
                }),

            unlockAllUpgrades: () =>
                set(() => {
                    const allUpgrades = UPGRADES.reduce((acc, u) => {
                        acc[u.id] = true;
                        return acc;
                    }, {} as Record<string, boolean>);
                    return { upgrades: allUpgrades };
                }),

            mineAction: (res) => {
                const state = get();
                const rockDef = MINING_DATA.find(r => r.id === res);
                if (!rockDef) return false; // Not a mining resource

                const miningLevel = state.skills.mining?.level ?? 1;
                if (miningLevel < rockDef.level) return false; // Too low level


                // --- Helper for Effect Multipliers ---
                const getMultiplier = (type: string, target: string) => {
                    let mult = 1;
                    for (const u of UPGRADES) {
                        if (state.upgrades[u.id]) {
                            for (const effect of u.effects) {
                                if (effect.type === type && (effect.target === target || effect.target === 'all')) {
                                    mult *= (effect.value ?? 1);
                                }
                            }
                        }
                    }
                    return mult;
                };

                // Check Cooldown
                const now = Date.now();
                if (state.miningReadyAt > now) return false;

                // Execute action
                let amount = 1;
                let xp = rockDef.baseXp;

                // --- Calculate Cooldown ---
                let cooldown = rockDef.baseCooldown ?? 1000;

                // 1. Level Reduction (0.5% per level, e.g. level 10 = 5% faster, level 100 = 50% faster)
                // Use a curve that doesn't hit 0. 
                // Formula: base / (1 + level * 0.05) -> Level 1 ~0.95, Level 20 ~0.5, Level 100 ~0.16
                // User said "reduced with levels".
                const levelFactor = 1 / (1 + (miningLevel * 0.05));
                cooldown *= levelFactor;

                // 2. Upgrade Reduction (mining_speed)
                const speedMult = getMultiplier('mining_speed', 'mining');
                // mining_speed usually means "faster", so 1.5x speed means 1/1.5 cooldown?
                // The upgrades are defined as "value: 0.98" for speed increase (reduction of time)?
                // Let's check `mining_cardio_1`: "value: 0.98", description "+2% Mining Speed". 
                // If value is < 1, it's a direct multiplier to duration.
                // If value is > 1 (e.g. 1.5), and it means speed, then duration should be / value.
                // Let's assume standard is "multiplier to duration" if < 1.
                // Let's look at `farming_speed`: "value: 0.75" -> duration *= 0.75.
                // So yes, multipliers are direct multipliers to duration.
                cooldown *= speedMult;

                // Minimum cooldown (except for 0 cooldown rocks)
                if (rockDef.baseCooldown > 0) {
                    cooldown = Math.max(100, cooldown); // Min 100ms
                } else {
                    cooldown = 0;
                }

                // Set new ready time
                set({ miningReadyAt: now + cooldown });

                // --- Luck / Double Drop Logic ---
                // Base chance 0? Or from mastery? Let's use upgrades.
                // "Miner's Luck": chance to double
                // For now, standard multiplier

                amount *= getMultiplier('multiply_yield', res);
                xp *= getMultiplier('multiply_xp', 'mining');

                // Critical Fracture Logic (Example unique mechanic from list)
                // "Category B: Active Mining... 5% chance ... 10x Damage" 
                // Since this is instant click, "Damage" isn't relevant, but "Instant Finish" is.
                // Let's interpret "Critical Fracture" as chance for extra drop

                state.addResource(res, Math.floor(amount));
                state.gainXp('mining', Math.floor(xp));
                return true;
            },

            plantAction: (plotId, crop) => {
                const state = get();
                const plot = state.farming.plots.find(p => p.id === plotId);

                // Unlock Check: Store OR Level
                const requiredLevel = PLOT_UNLOCK_LEVELS[plotId];
                const farmingLevel = state.skills.farming?.level ?? 1;
                const isUnlocked = plot?.unlocked || (requiredLevel !== undefined && farmingLevel >= requiredLevel);

                if (!plot || !isUnlocked || plot.crop) return false;

                const cropDef = FARMING_DATA.find(c => c.id === crop);
                if (!cropDef) return false;

                // Apply upgrades to Farming Speed (duration)
                let duration = cropDef.duration;
                // Helper to check upgrades
                const getSpeedMult = () => {
                    let mult = 1;
                    for (const u of UPGRADES) {
                        if (state.upgrades[u.id]) {
                            for (const e of u.effects) {
                                if (e.type === 'farming_speed') mult *= (e.value ?? 1);
                            }
                        }
                    }
                    return mult;
                }
                duration *= getSpeedMult();

                // Check Instant Grow Chance
                let instantGrow = false;
                for (const u of UPGRADES) {
                    if (state.upgrades[u.id]) {
                        for (const e of u.effects) {
                            if (e.type === 'instant_grow_chance') {
                                if (Math.random() < (e.value ?? 0)) {
                                    instantGrow = true;
                                }
                            }
                        }
                    }
                }
                if (instantGrow) duration = 0;

                if (farmingLevel < cropDef.level) return false;
                // Seed Cost Reduction
                let cost = cropDef.cost;
                for (const u of UPGRADES) {
                    if (state.upgrades[u.id]) {
                        for (const e of u.effects) {
                            if (e.type === 'seed_cost_reduction') {
                                cost *= (1 - (e.value ?? 0));
                            }
                        }
                    }
                }
                cost = Math.floor(cost);
                if (state.resources.gold < cost) return false; // Seed cost in gold

                set(state => ({
                    resources: { ...state.resources, gold: state.resources.gold - cost },
                    farming: {
                        ...state.farming,
                        plots: state.farming.plots.map(p =>
                            p.id === plotId
                                ? { ...p, crop: { type: crop, plantedAt: Date.now(), duration } }
                                : p
                        )
                    }
                }));
                return true;
            },

            harvestAction: (plotId) => {
                const state = get();
                const plot = state.farming.plots.find(p => p.id === plotId);
                if (!plot || !plot.crop) return false;

                if (Date.now() < plot.crop.plantedAt + plot.crop.duration) return false;

                const cropDef = FARMING_DATA.find(c => c.id === plot.crop!.type);
                if (!cropDef) return false;

                let yieldAmount = cropDef.yield;
                let xpAmount = cropDef.xp;

                // Super Charge Effect (2x Yield)
                if (plot.crop.isSuperCharged) {
                    yieldAmount *= 2;
                }

                // Apply upgrades
                for (const u of UPGRADES) {
                    if (state.upgrades[u.id]) {
                        for (const e of u.effects) {
                            if (e.type === 'multiply_yield' && (e.target === plot.crop!.type || e.target === 'all')) {
                                yieldAmount *= (e.value ?? 1);
                            }
                            if (e.type === 'multiply_xp' && (e.target === 'farming' || e.target === 'all')) {
                                xpAmount *= (e.value ?? 1);
                            }
                        }
                    }
                }

                // Crit Harvest (e.g. 5x yield)
                for (const u of UPGRADES) {
                    if (state.upgrades[u.id]) {
                        for (const e of u.effects) {
                            if (e.type === 'crit_harvest_chance') {
                                if (Math.random() < (e.value ?? 0)) {
                                    yieldAmount *= 5; // Fixed 5x mult for crit for now
                                }
                            }
                        }
                    }
                }

                // Seed Refund Chance
                let seedRefunded = false;
                for (const u of UPGRADES) {
                    if (state.upgrades[u.id]) {
                        for (const e of u.effects) {
                            if (e.type === 'seed_refund_chance') {
                                if (Math.random() < (e.value ?? 0)) {
                                    seedRefunded = true;
                                }
                            }
                        }
                    }
                }

                // Gold on Harvest
                let goldBonus = 0;
                for (const u of UPGRADES) {
                    if (state.upgrades[u.id]) {
                        for (const e of u.effects) {
                            if (e.type === 'gold_on_harvest') {
                                goldBonus += (e.value ?? 0);
                            }
                        }
                    }
                }

                // Super Charge Chance for NEXT crop (or just immediate bonus? Work item says "Single crop 'super charger' mechanics")
                // Let's implement a random chance to "Super Charge" a random OTHER plot upon harvest?
                // Or maybe just chance to find something extra?
                // Work item: "Single crop 'super charger' mechanics for special bonuses."
                // Interpretation: You can super charge a plot. Let's make it a 5% chance on harvest to Super Charge a valid empty plot?
                // Or maybe planting creates a super charged crop?
                // Let's do: 10% chance on harvest to Refund Seed + Instant Replant (Auto-Farming ish) or just large XP bonus.
                // Let's stick to simple: Chance to double yield was already in upgrades.
                // Let's make "Super Charge" a state that happens randomly during growth (need tick) or maybe 
                // just 5% chance on harvest to get a "Super Seed" refunded?
                // Let's implement: 1% Chance to Super Charge a specific plotId (unlock it or boost it).

                // Keep it simple as per plan: "Super Charge" chance/logic on harvest?
                // Plan said: "Calculate yield... Implement 'Super Charge' chance/logic... make it a chance to Super Charge a plot for 2x yield/speed."
                // Let's do: When you harvest, there is a small chance one of your EMPTY plots becomes "Super Charged" (glowing), and the next thing you plant there grows 2x fast or 2x yield.
                // Actually, let's keep it even simpler. This tool doesn't support complex logic changes easily without seeing the full file context for state updates.

                const resUpdates: Partial<Record<Resource, number>> = {
                    [plot.crop!.type]: (state.resources[plot.crop!.type] ?? 0) + Math.floor(yieldAmount)
                };
                if (goldBonus > 0 || seedRefunded) {
                    resUpdates.gold = (state.resources.gold ?? 0) + goldBonus;
                    if (seedRefunded) {
                        // Seed refund usually means GOLD refund since seeds purchase with gold? 
                        // Or do we have a seed item? Current impl: "cost: {gold: 10}". 
                        // So refund = add gold equal to cost.
                        resUpdates.gold += cropDef.cost;
                    }
                }

                set(state => ({
                    resources: { ...state.resources, ...resUpdates },
                    farming: {
                        ...state.farming,
                        plots: state.farming.plots.map(p =>
                            p.id === plotId
                                ? { ...p, crop: undefined } // clear crop
                                : p
                        )
                    }
                }));

                // Call gainXp after resource update
                get().gainXp('farming', Math.floor(xpAmount));

                return true;
            },

            assignGnome: (plotId) => {
                set(state => {
                    const unlockedGnomes = state.farming.gnomeHuts?.unlocked ?? 0;
                    const assignedCount = state.farming.plots.filter(p => p.assignedGnome).length;

                    if (assignedCount >= unlockedGnomes) {
                        // Check if this plot is already assigned, if so do nothing
                        const plot = state.farming.plots.find(p => p.id === plotId);
                        if (plot?.assignedGnome) return state;
                        // Else we cannot assign more
                        return state;
                    }

                    return {
                        farming: {
                            ...state.farming,
                            plots: state.farming.plots.map(p =>
                                p.id === plotId ? { ...p, assignedGnome: true } : p
                            )
                        }
                    };
                });
            },

            removeGnome: (plotId) => {
                set(state => ({
                    farming: {
                        ...state.farming,
                        plots: state.farming.plots.map(p =>
                            p.id === plotId ? { ...p, assignedGnome: false } : p
                        )
                    }
                }));
            },

            castRainTotem: () => {
                const state = get();
                // We use optional chaining for rainTotem
                if (!state.farming.rainTotem?.unlocked) return;
                const now = Date.now();
                if (now < state.farming.rainTotem.lastCast + state.farming.rainTotem.cooldown) return;

                // Effect: Advance all crops by 15 minutes (900000ms)
                const timeSkip = 15 * 60 * 1000;

                set(state => {
                    // Update rain totem state
                    // We must ensure rainTotem exists in state updates
                    const newRainTotem = { ...state.farming.rainTotem!, lastCast: now };

                    return {
                        farming: {
                            ...state.farming,
                            rainTotem: newRainTotem,
                            plots: state.farming.plots.map(p => {
                                if (p.crop) {
                                    return {
                                        ...p,
                                        crop: {
                                            ...p.crop,
                                            plantedAt: p.crop.plantedAt - timeSkip
                                        }
                                    };
                                }
                                return p;
                            })
                        }
                    };
                });
            },

            mutateCrop: (plotId) => {
                const state = get();
                // Cost: 100 Gold + 5 Herbs
                if (state.resources.gold < 100) return;
                if ((state.resources.herbs ?? 0) < 5) return;

                const plot = state.farming.plots.find(p => p.id === plotId);
                if (!plot || !plot.crop) return;

                // Prevent double mutation
                if (plot.crop.isMutated) return;

                // Pay Cost
                set(state => ({
                    resources: {
                        ...state.resources,
                        gold: state.resources.gold - 100,
                        herbs: (state.resources.herbs ?? 0) - 5
                    }
                }));

                const roll = Math.random();
                let newCrop = { ...plot.crop };
                let destroyed = false;

                if (roll < 0.5) {
                    // 50% Fail - Destroy
                    destroyed = true;
                } else if (roll < 0.9) {
                    // 40% Success - Mutated
                    newCrop.isMutated = true;
                    // Reset plantedAt? No, just keep growing but better
                } else {
                    // 10% Crit - Instant Finish + Mutated
                    newCrop.isMutated = true;
                    // Make it ready: plantedAt = now - duration - 1
                    newCrop.plantedAt = Date.now() - newCrop.duration - 1000;
                }

                set(state => ({
                    farming: {
                        ...state.farming,
                        plots: state.farming.plots.map(p =>
                            p.id === plotId ? { ...p, crop: destroyed ? undefined : newCrop } : p
                        )
                    }
                }));
            },

            startCraft: (slotId, recipeId) => {
                const state = get();
                const slot = state.crafting.slots.find(s => s.id === slotId);
                const recipe = CRAFTING_RECIPES.find(r => r.id === recipeId);

                if (!slot || !slot.unlocked || slot.activeCraft) return false;
                if (!recipe) return false;

                // Check Level
                if ((state.skills.crafting?.level ?? 1) < recipe.level) return false;

                // Check inputs
                for (const [res, amount] of Object.entries(recipe.input)) {
                    if ((state.resources[res as Resource] ?? 0) < (amount as number)) return false;
                }

                // Deduct resources
                const newResources = { ...state.resources };

                // Helper to check for resource preservation
                const getPreservationChance = () => {
                    let chance = 0;
                    for (const u of UPGRADES) {
                        if (state.upgrades[u.id]) {
                            for (const e of u.effects) {
                                if (e.type === 'resource_preservation') {
                                    chance += (e.value ?? 0);
                                }
                            }
                        }
                    }
                    return chance;
                };
                const preservationChance = getPreservationChance();

                for (const [res, amount] of Object.entries(recipe.input)) {
                    // Check preservation for each unit? Or per batch? 
                    // Let's do a simple check: "Chance to save ingredients".
                    // If triggered, we save the entire input stack for this craft? 
                    // Or per individual item? Let's do per-resource-type check for simplicity.
                    if (Math.random() < preservationChance) {
                        // Preserved!
                        continue;
                    }
                    newResources[res as Resource] -= (amount as number);
                }

                // Calculate Duration with Speed Multipliers
                let duration = recipe.duration;
                const getCraftSpeedMult = () => {
                    let mult = 1;
                    for (const u of UPGRADES) {
                        if (state.upgrades[u.id]) {
                            for (const e of u.effects) {
                                if (e.type === 'crafting_speed' && (e.target === 'all' || e.target === 'crafting')) {
                                    mult *= (e.value ?? 1);
                                }
                            }
                        }
                    }
                    return mult;
                };
                duration *= getCraftSpeedMult();
                // Cap minimum duration (e.g. 100ms)
                duration = Math.max(100, duration);

                // Start craft
                set(state => ({
                    resources: newResources,
                    crafting: {
                        ...state.crafting,
                        slots: state.crafting.slots.map(s => s.id === slotId ? {
                            ...s,
                            activeCraft: {
                                recipeId,
                                startTime: Date.now(),
                                duration
                            }
                        } : s)
                    }
                }));

                return true;
            },

            claimCraft: (slotId) => {
                const state = get();
                const slot = state.crafting.slots.find(s => s.id === slotId);
                if (!slot || !slot.activeCraft) return false;

                const { activeCraft } = slot;
                const recipe = CRAFTING_RECIPES.find(r => r.id === activeCraft.recipeId);
                if (!recipe) return false; // Should not happen

                if (Date.now() < activeCraft.startTime + activeCraft.duration) return false;

                // Check Double Craft Chance
                // Helper
                const getDoubleCraftChance = () => {
                    let chance = 0;
                    for (const u of UPGRADES) {
                        if (state.upgrades[u.id]) {
                            for (const e of u.effects) {
                                if (e.type === 'double_craft' && (e.target === 'all' || e.target === recipe.category)) {
                                    chance += (e.value ?? 0);
                                }
                            }
                        }
                    }
                    return chance;
                };

                const doubleChance = getDoubleCraftChance();
                const isDouble = Math.random() < doubleChance;
                const multiplier = isDouble ? 2 : 1;

                // Add Outputs
                const newResources = { ...state.resources };
                for (const [res, amount] of Object.entries(recipe.output)) {
                    newResources[res as Resource] = (newResources[res as Resource] ?? 0) + ((amount as number) * multiplier);
                }

                // Add XP (and multipliers)
                let xp = recipe.xp;
                // XP Multiplier
                const getXpMult = () => {
                    let mult = 1;
                    for (const u of UPGRADES) {
                        if (state.upgrades[u.id]) {
                            for (const e of u.effects) {
                                if (e.type === 'multiply_xp' && (e.target === 'crafting' || e.target === 'all')) {
                                    mult *= (e.value ?? 1);
                                }
                            }
                        }
                    }
                    return mult;
                }
                xp *= getXpMult();


                set(state => ({
                    resources: newResources,
                    crafting: {
                        ...state.crafting,
                        slots: state.crafting.slots.map(s => s.id === slotId ? { ...s, activeCraft: undefined } : s)
                    }
                }));

                get().gainXp('crafting', xp);

                return true;
            },

            startAutoCraft: (slotId, recipeId) => {
                set(state => ({
                    crafting: {
                        ...state.crafting,
                        slots: state.crafting.slots.map(s => s.id === slotId ? { ...s, assignedRecipeId: recipeId } : s)
                    }
                }));
                // Try to start immediately
                get().startCraft(slotId, recipeId);
            },

            stopAutoCraft: (slotId) => {
                set(state => ({
                    crafting: {
                        ...state.crafting,
                        slots: state.crafting.slots.map(s => s.id === slotId ? { ...s, assignedRecipeId: undefined } : s)
                    }
                }));
            },

            setMarketPrice: (res, price) =>
                set((state) => {
                    const item = state.market[res];
                    if (!item) return state;

                    // Apply 'negotiation' effect (market_benefit)
                    // "Market prices are 10% more favorable"
                    // If we Sell, price is HIGHER. If we Buy, price is LOWER?
                    // But here we are setting the GLOBAL market price derived from simulation/randomness.
                    // The "negotiation" benefit usually applies to the listing price limit or fee?
                    // Description: "Market prices are 10% more favorable."
                    // Let's apply it when creating a listing?
                    // Or when processing sim fill chance?
                    // Let's leave setMarketPrice raw (it's the global price)
                    // and apply benefit in createListing or processSim.

                    const newHistory = [...item.history, price].slice(-20);
                    return {
                        market: {
                            ...state.market,
                            [res]: { ...item, currentPrice: price, history: newHistory },
                        },
                    };
                }),

            createListing: (slotId, itemId, type, amount, price) => {
                // Integer enforcement
                const safePrice = Math.floor(price);
                const safeAmount = Math.floor(amount);
                if (safePrice < 1 || safeAmount < 1) return false;

                const state = get();
                const slotIndex = state.marketSlots.findIndex(s => s.id === slotId);
                const slot = state.marketSlots[slotIndex];

                if (!slot || !slot.unlocked || slot.listing) return false;

                // Check costs
                if (type === 'buy') {
                    const cost = safePrice * safeAmount;
                    if (state.resources.gold < cost) return false;

                    set(state => ({
                        resources: { ...state.resources, gold: state.resources.gold - cost },
                        marketSlots: state.marketSlots.map(s => s.id === slotId ? {
                            ...s,
                            listing: {
                                id: Math.random().toString(36).substr(2, 9),
                                itemId,
                                type,
                                pricePerUnit: safePrice,
                                totalQuantity: safeAmount,
                                filledQuantity: 0,
                                claimedQuantity: 0,
                                completed: false,
                                createdAt: Date.now()
                            }
                        } : s)
                    }));
                } else {
                    // Sell
                    if ((state.resources[itemId] ?? 0) < safeAmount) return false;

                    set(state => ({
                        resources: { ...state.resources, [itemId]: state.resources[itemId] - safeAmount },
                        marketSlots: state.marketSlots.map(s => s.id === slotId ? {
                            ...s,
                            listing: {
                                id: Math.random().toString(36).substr(2, 9),
                                itemId,
                                type,
                                pricePerUnit: safePrice,
                                totalQuantity: safeAmount,
                                filledQuantity: 0,
                                claimedQuantity: 0,
                                completed: false,
                                createdAt: Date.now()
                            }
                        } : s)
                    }));
                }
                return true;
            },

            cancelListing: (slotId) => {
                const state = get();
                const slot = state.marketSlots.find(s => s.id === slotId);
                if (!slot || !slot.listing) return false;

                const { listing } = slot;
                const remaining = listing.totalQuantity - listing.filledQuantity;
                const claimedSoFar = listing.claimedQuantity || 0;
                const claimable = listing.filledQuantity - claimedSoFar;

                // Refund remaining input
                const refundDetails: Partial<Record<Resource, number>> = {};

                if (listing.type === 'buy') {
                    refundDetails.gold = (state.resources.gold) + (remaining * listing.pricePerUnit);
                } else {
                    refundDetails[listing.itemId] = (state.resources[listing.itemId] ?? 0) + remaining;
                }

                // Claim any unclaimed filled items/gold
                if (claimable > 0) {
                    if (listing.type === 'buy') {
                        refundDetails[listing.itemId] = (refundDetails[listing.itemId] ?? state.resources[listing.itemId] ?? 0) + claimable;
                    } else {
                        refundDetails.gold = (refundDetails.gold ?? state.resources.gold) + (claimable * listing.pricePerUnit);
                    }
                }

                // Add to history if anything was claimed
                let newHistory = state.tradeHistory;
                if (claimable > 0) {
                    const historyItem: import('../types').TradeHistoryItem = {
                        id: Math.random().toString(36).substr(2, 9),
                        itemId: listing.itemId,
                        type: listing.type,
                        amount: claimable,
                        pricePerUnit: listing.pricePerUnit,
                        timestamp: Date.now()
                    };
                    newHistory = [historyItem, ...state.tradeHistory].slice(0, 100000); // Keep last 100,000
                }

                set(state => ({
                    resources: { ...state.resources, ...refundDetails },
                    tradeHistory: newHistory,
                    marketSlots: state.marketSlots.map(s => s.id === slotId ? { ...s, listing: undefined } : s)
                }));
                return true;
            },

            claimListing: (slotId) => {
                const state = get();
                const slot = state.marketSlots.find(s => s.id === slotId);
                if (!slot || !slot.listing) return false;

                const { listing } = slot;
                const claimedSoFar = listing.claimedQuantity || 0;
                const claimable = listing.filledQuantity - claimedSoFar;

                if (claimable <= 0) return false;

                const claimDetails: Partial<Record<Resource, number>> = {};
                if (listing.type === 'buy') {
                    claimDetails[listing.itemId] = (state.resources[listing.itemId] ?? 0) + claimable;
                } else {
                    claimDetails.gold = (state.resources.gold) + (claimable * listing.pricePerUnit);
                }

                const newClaimed = claimedSoFar + claimable;
                const shouldRemove = listing.completed && newClaimed >= listing.totalQuantity;

                // Add to history
                const historyItem: import('../types').TradeHistoryItem = {
                    id: Math.random().toString(36).substr(2, 9),
                    itemId: listing.itemId,
                    type: listing.type,
                    amount: claimable,
                    pricePerUnit: listing.pricePerUnit,
                    timestamp: Date.now()
                };

                set(state => ({
                    resources: { ...state.resources, ...claimDetails },
                    tradeHistory: [historyItem, ...state.tradeHistory].slice(0, 100000),
                    marketSlots: state.marketSlots.map(s => s.id === slotId ? {
                        ...s,
                        listing: shouldRemove ? undefined : { ...s.listing!, claimedQuantity: newClaimed }
                    } : s)
                }));
                return true;
            },

            unlockNextSlot: () => {
                const state = get();
                const nextSlot = state.marketSlots.find(s => !s.unlocked);
                if (!nextSlot) return false;

                // Cost formula: 100 * 10^slotIndex (exponential)
                // or simpler: 1000 * slotIndex
                const cost = 1000 * (nextSlot.id + 1);

                if (state.resources.gold < cost) return false;

                set(state => ({
                    resources: { ...state.resources, gold: state.resources.gold - cost },
                    marketSlots: state.marketSlots.map(s => s.id === nextSlot.id ? { ...s, unlocked: true } : s)
                }));
                return true;
            },

            processMarketSim: () => {
                set(state => {
                    let changed = false;
                    const newSlots = state.marketSlots.map(slot => {
                        if (!slot.listing || slot.listing.completed) return slot;

                        const item = state.market[slot.listing.itemId];
                        if (!item) return slot;

                        // Simulation Logic
                        // Chance to fill depends on price difference
                        const listPrice = slot.listing.pricePerUnit;
                        const marketPrice = item.currentPrice;

                        let fillChance = 0.05; // Base chance 5% per tick (which is 1s or 60s?)
                        // If 60s tick, chance should be higher. we'll see.

                        if (slot.listing.type === 'buy') {
                            // Buying: Higher price than market = better chance
                            if (listPrice >= marketPrice) {
                                const ratio = listPrice / marketPrice;
                                fillChance = 0.1 * ratio;
                            } else {
                                // Buying too low
                                fillChance = 0.01 * (listPrice / marketPrice);
                            }
                        } else {
                            // Selling: Lower price than market = better chance
                            if (listPrice <= marketPrice) {
                                const ratio = marketPrice / listPrice;
                                fillChance = 0.1 * ratio;
                            } else {
                                fillChance = 0.01 * (marketPrice / listPrice);
                            }
                        }

                        if (Math.random() < fillChance) {
                            const fillAmount = Math.ceil(Math.random() * (slot.listing.totalQuantity * 0.5));
                            const actualFill = Math.min(fillAmount, slot.listing.totalQuantity - slot.listing.filledQuantity);

                            if (actualFill > 0) {
                                changed = true;
                                const newFilled = slot.listing.filledQuantity + actualFill;
                                const completed = newFilled >= slot.listing.totalQuantity;
                                return {
                                    ...slot,
                                    listing: {
                                        ...slot.listing,
                                        filledQuantity: newFilled,
                                        completed
                                    }
                                };
                            }
                        }
                        return slot;
                    });

                    if (!changed) return state;
                    return { marketSlots: newSlots };
                });
            },

            tick: (dt) => {
                const now = Date.now();
                set({ lastSaveTime: now });

                // Handle Auto-Crafting
                const state = get();
                state.crafting.slots.forEach(slot => {
                    if (slot.assignedRecipeId) {
                        // 1. If active craft is done, claim it
                        if (slot.activeCraft && now >= slot.activeCraft.startTime + slot.activeCraft.duration) {
                            get().claimCraft(slot.id);
                        }

                        // 2. If no active craft (or just claimed), try to start
                        // Refetch slot after potential claim
                        const currentSlot = get().crafting.slots.find(s => s.id === slot.id);
                        if (currentSlot && !currentSlot.activeCraft) {
                            if (!get().startCraft(currentSlot.id, currentSlot.assignedRecipeId!)) {
                                // Failed to start (no resources?), do nothing, wait for next tick
                            }
                        }
                    }
                });

                // Handle Drone Mining (Idle)
                get().tickDrones(dt);

                // Handle Gnome Farming
                get().farming.plots.forEach(plot => {
                    if (plot.assignedGnome && plot.crop) {
                        if (now >= plot.crop.plantedAt + plot.crop.duration) {
                            const cropType = plot.crop.type;
                            // Harvest
                            if (get().harvestAction(plot.id)) {
                                // Replant same crop
                                get().plantAction(plot.id, cropType);
                            }
                        }
                    }
                });
            },

            purchaseUpgrade: (upgradeId) => {
                const state = get();
                if (state.upgrades[upgradeId]) return false; // Already purchased

                const upgrade = UPGRADES.find(u => u.id === upgradeId);
                if (!upgrade) return false;

                // Check prerequisites (Other upgrades)
                if (upgrade.prerequisites) {
                    for (const prereqId of upgrade.prerequisites) {
                        if (!state.upgrades[prereqId]) return false;
                    }
                }

                // Check cost
                if (upgrade.cost) {
                    for (const [res, amount] of Object.entries(upgrade.cost)) {
                        const costAmount = amount ?? 0;
                        if ((state.resources[res as Resource] ?? 0) < costAmount) return false;
                        if (!state.removeResource(res as Resource, costAmount)) return false; // Double check
                    }
                }

                // Apply immediate effects (like unlocking slots)
                // Passive effects are checked in actions (mineAction etc)
                // Handle special immediate effects
                upgrade.effects.forEach(effect => {
                    if (effect.type === 'unlock_mechanic' && effect.target === 'extra_market_slots') {
                        // We need to add market slots
                        // This is tricky because marketSlots is an array.
                        // Let's handle it by unlocking next locked slots or pushing new ones?
                        // Current system is fixed 10 slots.
                        // Let's just say it unlocks slots if they are locked?
                        // "Unlock +2 Market Slots immediately"
                        // We track unlocked status.
                        // Let's find 2 locked slots and unlock them.
                        // Logic for market slots (simplified for now as focus is crafting)
                        const lockedSlots = state.marketSlots.filter(s => !s.unlocked);
                        for (let i = 0; i < (effect.value ?? 1); i++) {
                            if (lockedSlots[i]) {
                                // We need to map back to original index to update state correctly below
                                // But since we are iterating, we can just do a map update
                            }
                        }
                        // Actually, let's keep it simple: Map state
                        set(prev => {
                            let count = effect.value ?? 1;
                            return {
                                marketSlots: prev.marketSlots.map(s => {
                                    if (!s.unlocked && count > 0) {
                                        count--;
                                        return { ...s, unlocked: true };
                                    }
                                    return s;
                                })
                            };
                        });
                    }
                    if (effect.type === 'unlock_mechanic' && effect.target === 'extra_craft_slot') {
                        // Unlock crafting slots
                        set(prev => {
                            let count = effect.value ?? 1;
                            return {
                                crafting: {
                                    ...prev.crafting,
                                    slots: prev.crafting.slots.map(s => {
                                        if (!s.unlocked && count > 0) {
                                            count--;
                                            return { ...s, unlocked: true };
                                        }
                                        return s;
                                    })
                                }
                            };
                        });
                    }
                    if (effect.type === 'unlock_mechanic' && effect.target === 'extra_farming_plot') {
                        set(prev => {
                            let count = effect.value ?? 1;
                            return {
                                farming: {
                                    ...prev.farming,
                                    plots: prev.farming.plots.map(p => {
                                        if (!p.unlocked && count > 0) {
                                            count--;
                                            return { ...p, unlocked: true };
                                        }
                                        return p;
                                    })
                                }
                            };
                        });
                    }
                    if (effect.type === 'unlock_mechanic' && effect.target === 'gnome_huts') {
                        set(prev => ({
                            farming: {
                                ...prev.farming,
                                gnomeHuts: {
                                    unlocked: (prev.farming.gnomeHuts?.unlocked ?? 0) + (effect.value ?? 1)
                                }
                            }
                        }));
                    }
                    if (effect.type === 'unlock_mechanic' && effect.target === 'rain_totem') {
                        set(prev => ({
                            farming: {
                                ...prev.farming,
                                rainTotem: {
                                    unlocked: true,
                                    lastCast: 0,
                                    cooldown: 10 * 60 * 1000 // 10 minutes
                                }
                            }
                        }));
                    }
                    if (effect.type === 'unlock_mechanic' && effect.target === 'genetic_mutator') {
                        set(prev => ({
                            farming: {
                                ...prev.farming,
                                mutator: { unlocked: true }
                            }
                        }));
                    }

                    // Drone unlock mechanics
                    if (effect.type === 'unlock_mechanic' && effect.target?.startsWith('drone_tier_')) {
                        const tierStr = effect.target.replace('drone_tier_', '');
                        const tier = parseInt(tierStr, 10);

                        if (tier > 0) {
                            set(prev => {
                                // Only add if tier not already unlocked
                                if (prev.drones.unlockedTiers.includes(tier)) return prev;

                                const newDrone: DroneState = {
                                    id: `drone-${tier}-${Date.now()}`,
                                    tier,
                                    targetOre: null,
                                    progress: 0,
                                    lastTickTime: Date.now()
                                };

                                return {
                                    drones: {
                                        ...prev.drones,
                                        unlockedTiers: [...prev.drones.unlockedTiers, tier],
                                        activeDrones: [...prev.drones.activeDrones, newDrone],
                                        maxDrones: prev.drones.maxDrones + 1
                                    }
                                };
                            });
                        }
                    }
                });

                set(state => ({
                    upgrades: { ...state.upgrades, [upgradeId]: true }
                }));

                return true;
            },

            setLastListingItem: (item) => set({ lastListingItem: item }),
            setLastListingType: (type) => set({ lastListingType: type }),

            throwDynamite: (score: number, targetRes: Resource) => {
                const state = get();
                if ((state.resources['dynamite'] ?? 0) < 1) return null;

                // Validate Target - must be a mining resource
                const rockDef = MINING_DATA.find(r => r.id === targetRes);
                if (!rockDef) return null;

                // Must be unlocked
                const miningLevel = state.skills.mining?.level ?? 1;
                if (miningLevel < rockDef.level) return null;

                // Consume Dynamite
                if (!state.removeResource('dynamite', 1)) return null;

                // Calculate Reward based on Score (0-100)
                let multiplier = 1.0;
                if (score >= 95) multiplier = 2.0;
                else if (score >= 70) multiplier = 1.5;
                else if (score >= 40) multiplier = 1.2;

                const baseAmount = 5;
                const bonusAmount = Math.floor(Math.random() * 4); // 0-3
                const totalAmount = Math.floor((baseAmount + bonusAmount) * multiplier);

                const rewards: Partial<Record<Resource, number>> = {};
                rewards[targetRes] = totalAmount;

                // Add to inventory
                set(state => ({
                    resources: { ...state.resources, [targetRes]: (state.resources[targetRes] ?? 0) + totalAmount }
                }));

                // Add XP
                const xp = Math.floor(rockDef.baseXp * 0.5 * totalAmount);
                get().gainXp('mining', xp);

                return rewards;
            },

            // --- DRONE ACTIONS ---
            setDroneTarget: (droneId, ore) => {
                set(state => {
                    const drone = state.drones.activeDrones.find(d => d.id === droneId);
                    if (!drone) return state;

                    // If ore is set, validate it's mineable by this drone tier
                    if (ore) {
                        const rockDef = MINING_DATA.find(r => r.id === ore);
                        if (!rockDef) return state;
                        // Tier N drone can mine tier N and below
                        if (rockDef.tier > drone.tier) return state;
                        // Also check player level
                        const miningLevel = state.skills.mining?.level ?? 1;
                        if (miningLevel < rockDef.level) return state;
                    }

                    return {
                        drones: {
                            ...state.drones,
                            activeDrones: state.drones.activeDrones.map(d =>
                                d.id === droneId
                                    ? { ...d, targetOre: ore, progress: 0, lastTickTime: Date.now() }
                                    : d
                            )
                        }
                    };
                });
            },

            tickDrones: (dt) => {
                const state = get();
                if (state.drones.activeDrones.length === 0) return;

                const now = Date.now();
                let accumulatedXp = 0; // Track XP outside set

                set(state => {
                    let resourceChanges: Partial<Record<Resource, number>> = {};

                    const updatedDrones = state.drones.activeDrones.map(drone => {
                        if (!drone.targetOre) return drone;

                        const rockDef = MINING_DATA.find(r => r.id === drone.targetOre);
                        if (!rockDef) return drone;

                        // Drone speed: ore.baseCooldown * 10 (10x slower)
                        // If baseCooldown is 0, use 5000ms as minimum
                        const droneSpeed = Math.max(5000, (rockDef.baseCooldown || 500) * 10);

                        // Calculate progress gained (progress is 0-100)
                        const progressGain = (dt / droneSpeed) * 100;
                        let newProgress = drone.progress + progressGain;

                        // Check if we completed a cycle
                        while (newProgress >= 100) {
                            newProgress -= 100;
                            // Add resource
                            resourceChanges[drone.targetOre] = (resourceChanges[drone.targetOre] ?? 0) + 1;
                            // Add XP (50% of normal XP for idle)
                            accumulatedXp += rockDef.baseXp * 0.5;
                        }

                        return {
                            ...drone,
                            progress: newProgress,
                            lastTickTime: now
                        };
                    });

                    // Apply resource changes
                    const newResources = { ...state.resources };
                    for (const [res, amount] of Object.entries(resourceChanges)) {
                        newResources[res as Resource] = (newResources[res as Resource] ?? 0) + amount;
                    }

                    return {
                        drones: {
                            ...state.drones,
                            activeDrones: updatedDrones
                        },
                        resources: newResources
                    };
                });

                // Apply accumulated XP after set
                if (accumulatedXp > 0) {
                    get().gainXp('mining', Math.floor(accumulatedXp));
                }
            },
        }),
        {
            name: 'idlescape-storage',
            merge: (persistedState: any, currentState: StoreState) => {
                const merged = { ...currentState, ...persistedState };

                // Merge Market: Ensure all initial market items exist
                const initialMarket = currentState.market;
                const savedMarket = persistedState.market || {};
                const combinedMarket = { ...savedMarket };

                Object.keys(initialMarket).forEach(key => {
                    const resKey = key as Resource;
                    if (!combinedMarket[resKey]) {
                        combinedMarket[resKey] = initialMarket[resKey];
                    }
                });
                merged.market = combinedMarket;

                // Merge Skills: Ensure all new skills exist
                const initialSkills = currentState.skills;
                const savedSkills = persistedState.skills || {};
                const combinedSkills = { ...savedSkills };

                Object.keys(initialSkills).forEach(key => {
                    if (!combinedSkills[key]) {
                        combinedSkills[key] = initialSkills[key];
                    }
                });
                merged.skills = combinedSkills;

                // Merge Crafting Slots if missing
                if (!merged.crafting || !merged.crafting.slots) {
                    merged.crafting = currentState.crafting;
                }

                // Merge Drones state if missing
                if (!merged.drones) {
                    merged.drones = currentState.drones;
                }

                return merged;
            }
        }
    )
);
